import { format, isValid, parseISO } from 'date-fns';
import XlsxPopulate from 'xlsx-populate/browser/xlsx-populate';
import { getQldMobile } from '../qldContact';

const GCIT_TEMPLATE_URL = '/templates/GCIT Report Template V2.7.xlsx';
const GCIT_TEMPLATE_SHEET_NAME = 'GCIT Report Template';

export const GCIT_RTO_NUMBER = '45708';
export const GCIT_CARD_STATUS = 'Issued';
export const GCIT_DELIVERY_REASON = 'Connected Real Time Delivery';
export const GCIT_ISSUED_FROM_POSTCODE = '2205';

const trimValue = (value = '') => String(value || '').trim();

const toValidDate = (value) => {
    if (!value) {
        return null;
    }

    if (value instanceof Date && isValid(value)) {
        return value;
    }

    const parsedDate = typeof value === 'string' ? parseISO(value) : new Date(value);
    return isValid(parsedDate) ? parsedDate : null;
};

const buildStreetAddress = (student = {}) => {
    const fullAddress = trimValue(student.address);
    const streetNumber = trimValue(student.streetNumber);
    const streetName = trimValue(student.streetName);
    const composedStreet = [streetNumber, streetName].filter(Boolean).join(' ').trim();

    if (composedStreet) {
        return composedStreet;
    }

    return fullAddress;
};

const buildIssueDate = (student = {}) => (
    toValidDate(
        student?.stages?.declaration?.completedAt
        || student?.generatedPdfAt
        || student?.updatedAt
        || student?.createdAt
    ) || new Date()
);

const buildCardNumber = (student = {}) => (
    trimValue(
        student.cardNumber
        || student.gcitCardNumber
        || student.responses?.declaration?.cardNumber
        || student.responses?.declaration?.gcitCardNumber
    )
);

const buildContactNumber = getQldMobile;

const setCellValue = (sheet, cellRef, value) => {
    sheet.cell(cellRef).value(value);
};

export const buildEligibleGcitStudents = (students = [], canGenerateStudentPdf) => (
    students.filter((student) => canGenerateStudentPdf(student))
);

export const generateGcitReportTemplate = async ({
    students = [],
    canGenerateStudentPdf,
}) => {
    const eligibleStudents = buildEligibleGcitStudents(students, canGenerateStudentPdf);

    if (eligibleStudents.length === 0) {
        throw new Error('No completed students are available for the GCIT report template.');
    }

    const response = await fetch(GCIT_TEMPLATE_URL);
    if (!response.ok) {
        throw new Error('Unable to load the GCIT report template workbook.');
    }

    const workbook = await XlsxPopulate.fromDataAsync(await response.arrayBuffer());
    const sheet = workbook.sheet(GCIT_TEMPLATE_SHEET_NAME);
    const issueDates = eligibleStudents.map(buildIssueDate);
    const reportingMonth = issueDates[0] || new Date();

    setCellValue(sheet, 'A2', GCIT_RTO_NUMBER);
    setCellValue(sheet, 'D2', reportingMonth);

    eligibleStudents.forEach((student, index) => {
        const rowNumber = 4 + index;
        const issueDate = issueDates[index];
        const dateCancelled = '';
        const firstName = trimValue(student.firstName);
        const lastName = trimValue(student.lastName);
        const dob = toValidDate(student.dob);
        const address = buildStreetAddress(student);
        const suburb = trimValue(student.suburb);
        const state = trimValue(student.state).toUpperCase();
        const postcode = trimValue(student.postcode);
        const contactNumber = buildContactNumber(student);
        const email = trimValue(student.email);

        setCellValue(sheet, `A${rowNumber}`, buildCardNumber(student));
        setCellValue(sheet, `B${rowNumber}`, GCIT_CARD_STATUS);
        setCellValue(sheet, `C${rowNumber}`, issueDate);
        setCellValue(sheet, `D${rowNumber}`, dateCancelled);
        setCellValue(sheet, `E${rowNumber}`, firstName);
        setCellValue(sheet, `F${rowNumber}`, lastName);
        setCellValue(sheet, `G${rowNumber}`, dob || '');
        setCellValue(sheet, `H${rowNumber}`, address);
        setCellValue(sheet, `I${rowNumber}`, suburb);
        setCellValue(sheet, `J${rowNumber}`, state);
        setCellValue(sheet, `K${rowNumber}`, postcode);
        setCellValue(sheet, `L${rowNumber}`, contactNumber);
        setCellValue(sheet, `M${rowNumber}`, email);
        setCellValue(sheet, `N${rowNumber}`, GCIT_DELIVERY_REASON);
        setCellValue(sheet, `O${rowNumber}`, '');
        setCellValue(sheet, `P${rowNumber}`, GCIT_ISSUED_FROM_POSTCODE);
    });

    const fileName = `GCIT Report Template ${format(reportingMonth, 'MMM yyyy')}.xlsx`;
    const blob = await workbook.outputAsync();

    return {
        blob,
        fileName,
        eligibleStudents,
    };
};
